import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DashboardCharts, DashboardKpis, LiveVehicle } from '@rfleet/shared';
import clsx from 'clsx';
import { AlertTriangle, Bell, Fuel, MapPin, Receipt, Route, Truck, Wrench } from 'lucide-react';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { Map as MlMap } from 'maplibre-gl';
import { Badge, EmptyState, Skeleton } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import { fmtAgo, fmtEgp, fmtNumber } from '../../lib/format';
import { useSocketEvent } from '../../lib/socket';
import { MapView } from '../../map/MapView';
import { VehiclesLayer, vehicleKind, type VehicleMarker } from '../../map/VehiclesLayer';
import { severityTone, type AlertRow } from '../alerts/types';
import { dueTone, type ScheduleRow } from '../maintenance/types';

const COLORS = ['#0F3D26', '#BEF264', '#16A34A', '#F59E0B', '#94A3B8', '#EF4444', '#155233'];

interface TripRow { id: string; number: string; status: string; vehicle: { plateAr: string } | null; driver: { fullName: string } | null; destText: string | null; dueAt: string | null; customer: { name: string } | null }

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const qc = useQueryClient();
  const mapRef = useRef<MlMap | null>(null);

  const kpis = useQuery({ queryKey: ['dashboard', 'kpis'], queryFn: () => api<DashboardKpis>('/dashboard/kpis'), refetchInterval: 60_000 });
  const charts = useQuery({ queryKey: ['dashboard', 'charts'], queryFn: () => api<DashboardCharts>('/dashboard/charts'), refetchInterval: 120_000 });
  const live = useQuery({ queryKey: ['live-vehicles'], queryFn: () => api<LiveVehicle[]>('/live/vehicles'), refetchInterval: 60_000 });
  const alerts = useQuery({ queryKey: ['alerts', 'dashboard'], queryFn: () => api<Paginated<AlertRow>>('/alerts?status=OPEN&pageSize=6') });
  const due = useQuery({ queryKey: ['maintenance', 'due', 'dashboard'], queryFn: () => api<Paginated<ScheduleRow>>('/maintenance/due') });
  const trips = useQuery({ queryKey: ['trips', 'dashboard'], queryFn: () => api<Paginated<TripRow>>('/trips?status=IN_PROGRESS&pageSize=6') });

  useSocketEvent('alert.new', useCallback(() => { void qc.invalidateQueries({ queryKey: ['alerts'] }); void qc.invalidateQueries({ queryKey: ['dashboard'] }); }, [qc]));
  useSocketEvent('alert.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['alerts'] }), [qc]));
  useSocketEvent('trip.updated', useCallback(() => void qc.invalidateQueries({ queryKey: ['trips', 'dashboard'] }), [qc]));

  const markers = useMemo<VehicleMarker[]>(
    () => (live.data ?? []).filter((v) => v.live.lat != null && v.live.lon != null).map((v) => ({ id: v.id, label: v.plateAr, lat: v.live.lat!, lon: v.live.lon!, heading: v.live.heading, status: v.live.status, speedKph: v.live.speedKph, kind: vehicleKind(v.type) })),
    [live.data],
  );
  const fit = useCallback((m: MlMap) => {
    mapRef.current = m;
    if (!markers.length) return;
    const lons = markers.map((x) => x.lon), lats = markers.map((x) => x.lat);
    m.fitBounds([[Math.min(...lons), Math.min(...lats)], [Math.max(...lons), Math.max(...lats)]], { padding: 40, maxZoom: 12, duration: 0 });
  }, [markers]);

  const k = kpis.data;
  const c = charts.data;
  const money = (n: number) => fmtEgp(n, lang);

  return (
    <div className="flex flex-col gap-5">
      {!k ? <Skeleton className="h-28" /> : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Kpi to="/live?status=MOVING" icon={Truck} label={t('dashboard.kpi.moving')} value={`${k.vehicles.byStatus.MOVING}`} hint={t('dashboard.kpi.ofVehicles', { count: k.vehicles.total })} />
          <Kpi to="/trips" icon={Route} label={t('dashboard.kpi.tripsToday')} value={`${k.trips.today}`} hint={t('dashboard.kpi.inProgress', { count: k.trips.inProgress })} tone={k.trips.lateNow ? 'warning' : undefined} />
          <Kpi to="/expenses?view=analytics" icon={Receipt} label={t('dashboard.kpi.expensesToday')} value={money(k.expenses.todayEgp)} hint={t('dashboard.kpi.month', { value: money(k.expenses.monthEgp) })} />
          <Kpi to="/expenses?view=fuel" icon={Fuel} label={t('dashboard.kpi.fuel')} value={k.fuel.avgLPer100Km != null ? `${fmtNumber(k.fuel.avgLPer100Km, lang, 1)} ${t('units.lPer100')}` : '—'} hint={`${fmtNumber(k.fuel.monthLiters, lang)} ${t('units.liter')} · ${money(k.fuel.monthEgp)}`} />
          <Kpi to="/alerts" icon={Bell} label={t('dashboard.kpi.openAlerts')} value={`${k.alerts.open}`} hint={t('dashboard.kpi.critical', { count: k.alerts.critical })} tone={k.alerts.critical ? 'danger' : undefined} />
          <Kpi to="/maintenance" icon={Wrench} label={t('dashboard.kpi.maintenanceDue')} value={`${k.maintenance.overdue + k.maintenance.dueSoon}`} hint={t('dashboard.kpi.overdue', { count: k.maintenance.overdue })} tone={k.maintenance.overdue ? 'warning' : undefined} />
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-3">
        <section className="card overflow-hidden !p-0 xl:col-span-2">
          <div className="flex items-center justify-between border-b border-border-soft px-4 py-3">
            <h2 className="flex items-center gap-2 text-sm font-bold"><MapPin size={16} /> {t('nav.live')}</h2>
            <div className="flex items-center gap-3 text-xs text-text-muted">
              {k ? (['MOVING', 'IDLE', 'STOPPED', 'OFFLINE'] as const).map((s) => <span key={s} className="flex items-center gap-1"><span className={clsx('size-2 rounded-full', DOT[s])} /> {t(`status.${s}`)} {k.vehicles.byStatus[s]}</span>) : null}
              <Link to="/live" className="font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link>
            </div>
          </div>
          <div className="h-[340px]">
            <MapView zoom={6} onReady={fit} interactive>
              <VehiclesLayer vehicles={markers} zoomOnClick />
            </MapView>
          </div>
        </section>

        <section className="card flex flex-col">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-bold"><AlertTriangle size={16} /> {t('dashboard.openAlerts')}</h2>
            <Link to="/alerts" className="text-xs font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link>
          </div>
          {alerts.isLoading ? <Skeleton className="h-40" /> : !alerts.data?.data.length ? <EmptyState title={t('dashboard.noAlerts')} /> : (
            <ul className="flex flex-col divide-y divide-border-soft text-sm">
              {alerts.data.data.map((a) => (
                <li key={a.id} className="flex items-start gap-2 py-2">
                  <Badge tone={severityTone(a.severity)} dot>{t(`alerts.severities.${a.severity}`)}</Badge>
                  <div className="min-w-0 flex-1">
                    <Link to={`/alerts/${a.id}`} className="line-clamp-2 font-medium hover:underline">{lang === 'en' ? a.titleEn : a.titleAr}</Link>
                    <div className="text-xs text-text-faint">{fmtAgo(a.occurredAt, lang)}</div>
                  </div>
                  {a.lat != null ? <Link to={`/live?vehicle=${a.vehicleId ?? ''}`} className="btn-ghost size-8 !p-0" aria-label={t('alerts.showOnMap')} title={t('alerts.showOnMap')}><MapPin size={14} /></Link> : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-bold"><Route size={16} /> {t('dashboard.activeTrips')}</h2>
            <Link to="/trips" className="text-xs font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link>
          </div>
          {trips.isLoading ? <Skeleton className="h-40" /> : !trips.data?.data.length ? <EmptyState title={t('dashboard.noTrips')} /> : (
            <ul className="flex flex-col divide-y divide-border-soft text-sm">
              {trips.data.data.map((tr) => (
                <li key={tr.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <Link to={`/trips/${tr.id}`} className="font-semibold hover:underline" dir="ltr">{tr.number}</Link>
                    <div className="truncate text-xs text-text-muted">{[tr.vehicle?.plateAr, tr.driver?.fullName, tr.destText].filter(Boolean).join(' · ')}</div>
                  </div>
                  {tr.dueAt && new Date(tr.dueAt) < new Date() ? <Badge tone="danger">{t('dashboard.late')}</Badge> : <Badge tone="success" dot>{t('trips.statuses.IN_PROGRESS')}</Badge>}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 text-sm font-bold"><Wrench size={16} /> {t('dashboard.maintenanceStatus')}</h2>
            <Link to="/maintenance" className="text-xs font-semibold text-brand-ink hover:underline">{t('common.viewAll')}</Link>
          </div>
          {due.isLoading ? <Skeleton className="h-40" /> : !due.data?.data.length ? <EmptyState title={t('dashboard.noMaintenance')} /> : (
            <ul className="flex flex-col divide-y divide-border-soft text-sm">
              {due.data.data.slice(0, 6).map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{s.title}</div>
                    <Link to={`/vehicles/${s.vehicle.id}`} className="text-xs text-text-muted hover:underline">{s.vehicle.plateAr}</Link>
                  </div>
                  <Badge tone={dueTone(s.due.status)}>{s.due.remaining != null ? `${fmtNumber(Math.abs(s.due.remaining), lang)} ${t(`units.${s.due.unit}`)}` : t(`maintenance.dueStatus.${s.due.status}`)}</Badge>
                </li>
              ))}
            </ul>
          )}
          {k ? <div className="mt-3 flex gap-2 text-xs text-text-muted"><span>{t('maintenance.kpi.openWorkOrders')}: <strong className="text-text">{k.maintenance.openWorkOrders}</strong></span><span>{t('maintenance.kpi.documentsExpiring')}: <strong className="text-text">{k.maintenance.documentsExpiring}</strong></span></div> : null}
        </section>

        <section className="card">
          <h2 className="mb-3 text-sm font-bold">{t('dashboard.expensesMonth')}</h2>
          {!c ? <Skeleton className="h-40" /> : !c.expensesByCategory.length ? <EmptyState title={t('common.empty')} /> : (
            <div className="flex items-center gap-3">
              <div className="h-40 w-40 flex-none" dir="ltr">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={c.expensesByCategory} dataKey="egp" nameKey={lang === 'en' ? 'nameEn' : 'nameAr'} innerRadius={40} outerRadius={65} paddingAngle={2}>
                      {c.expensesByCategory.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v: number) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
                {c.expensesByCategory.slice(0, 6).map((x, i) => (
                  <li key={x.categoryId} className="flex items-center gap-2"><span className="size-2 flex-none rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="flex-1 truncate">{lang === 'en' ? x.nameEn : x.nameAr}</span><span className="font-semibold tabular-nums">{money(x.egp)}</span></li>
                ))}
              </ul>
            </div>
          )}
        </section>

        <section className="card xl:col-span-2">
          <h2 className="mb-3 text-sm font-bold">{t('dashboard.distance14')}</h2>
          {!c ? <Skeleton className="h-40" /> : !c.distanceByDay.length ? <EmptyState title={t('common.empty')} /> : (
            <div className="h-44" dir="ltr">
              <ResponsiveContainer>
                <BarChart data={c.distanceByDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5EE" />
                  <XAxis dataKey="date" fontSize={11} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis fontSize={11} tickFormatter={(v: number) => fmtNumber(v, lang)} />
                  <Tooltip formatter={(v: number) => `${fmtNumber(v, lang)} ${t('units.km')}`} />
                  <Bar dataKey="km" name={t('units.km')} fill="#0F3D26" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="card">
          <h2 className="mb-3 text-sm font-bold">{t('dashboard.topVehicles')}</h2>
          {!c ? <Skeleton className="h-40" /> : !c.topVehiclesByExpense.length ? <EmptyState title={t('common.empty')} /> : (
            <ul className="flex flex-col gap-2 text-sm">
              {c.topVehiclesByExpense.map((v) => {
                const max = c.topVehiclesByExpense[0]!.egp;
                return (
                  <li key={v.vehicleId}>
                    <div className="flex items-center justify-between gap-2"><Link to={`/vehicles/${v.vehicleId}`} className="hover:underline">{v.plateAr}</Link><span className="font-semibold tabular-nums">{money(v.egp)}</span></div>
                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-surface-2"><div className="h-full rounded-full bg-brand" style={{ width: `${max ? Math.max(4, (v.egp / max) * 100) : 0}%` }} /></div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

const DOT: Record<string, string> = { MOVING: 'bg-success', IDLE: 'bg-warning', STOPPED: 'bg-neutral', OFFLINE: 'bg-neutral/50' };

function Kpi({ to, icon: Icon, label, value, hint, tone }: { to: string; icon: typeof Truck; label: string; value: string; hint?: string; tone?: 'warning' | 'danger' }) {
  return (
    <Link to={to} className={clsx('card flex items-start gap-3 !p-4 transition-colors hover:bg-surface-hover', tone === 'warning' && 'border-warning/40', tone === 'danger' && 'border-danger/40')}>
      <span className={clsx('flex size-9 flex-none items-center justify-center rounded-lg', tone === 'danger' ? 'bg-danger-bg text-danger-fg' : tone === 'warning' ? 'bg-warning-bg text-warning-fg' : 'bg-brand-soft text-brand-ink')}><Icon size={18} /></span>
      <span className="min-w-0">
        <span className="block text-xs font-semibold text-text-muted">{label}</span>
        <span className="block truncate text-xl font-bold tabular-nums">{value}</span>
        {hint ? <span className="block truncate text-xs text-text-faint">{hint}</span> : null}
      </span>
    </Link>
  );
}
